import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import AdminSidebar from "../components/AdminSidebar";
import AdminNoteModal from "../components/AdminNoteModal";
import "../styles/AdminDashboard.css";
import {
  Users,
  GraduationCap,
  ShieldCheck,
  Clock4,
  UserPlus,
  Group,
  Send,
  FileText,
  Plus,
  Trash2,
} from "lucide-react";

// Icon mapping for stats (constant, defined outside component)
const iconMap = {
  Kursanci: Users,
  Wykładowcy: GraduationCap,
  Administratorzy: ShieldCheck,
  "Ostatnie logowania (24h)": Clock4,
};

const ADMIN_NOTE_KEY = "admin-dashboard-note";

export default function AdminDashboard() {
  const [stats, setStats] = useState([]);
  const [statsLoading, setStatsLoading] = useState(true);
  const [upcomingClasses, setUpcomingClasses] = useState([]);
  const [upcomingClassesLoading, setUpcomingClassesLoading] = useState(true);
  const [adminNotes, setAdminNotes] = useState([]);
  const [adminNotesLoading, setAdminNotesLoading] = useState(true);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);

  // prywatny notes jednego admina (localStorage)
  const [adminNote, setAdminNote] = useState("");

  useEffect(() => {
    async function fetchStats() {
      try {
        const response = await axios.get('/dashboard/stats');
        // Map backend stats to include icons
        const statsWithIcons = response.data.stats.map((stat) => ({
          ...stat,
          icon: iconMap[stat.label] || Users,
        }));
        setStats(statsWithIcons);
      } catch (error) {
        console.error("Error fetching dashboard stats:", error);
        // Set empty stats on error
        setStats([]);
      } finally {
        setStatsLoading(false);
      }
    }

    async function fetchUpcomingClasses() {
      try {
        const response = await axios.get('/dashboard/upcoming-classes');
        setUpcomingClasses(response.data);
      } catch (error) {
        console.error("Error fetching upcoming classes:", error);
        setUpcomingClasses([]);
      } finally {
        setUpcomingClassesLoading(false);
      }
    }

    async function fetchAdminNotes() {
      try {
        const response = await axios.get('/admin-notes');
        setAdminNotes(response.data || []);
      } catch (error) {
        console.error("Error fetching admin notes:", error);
        setAdminNotes([]);
      } finally {
        setAdminNotesLoading(false);
      }
    }

    // Get user_id from localStorage
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        setCurrentUserId(user.user_id || user.id);
      }
    } catch (e) {
      console.warn("Cannot read user from localStorage:", e);
    }

    fetchStats();
    fetchUpcomingClasses();
    fetchAdminNotes();
  }, []);

  // wczytanie notatki z localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem(ADMIN_NOTE_KEY);
      if (stored !== null) {
        setAdminNote(stored);
      }
    } catch (e) {
      console.warn("Cannot read admin note from localStorage:", e);
    }
  }, []);

  // zapis notatki
  useEffect(() => {
    try {
      localStorage.setItem(ADMIN_NOTE_KEY, adminNote);
    } catch (e) {
      console.warn("Cannot save admin note to localStorage:", e);
    }
  }, [adminNote]);

  const handleNoteAdded = async () => {
    try {
      const response = await axios.get('/admin-notes');
      setAdminNotes(response.data || []);
    } catch (error) {
      console.error("Error refreshing admin notes:", error);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Czy na pewno chcesz usunąć tę notatkę?')) {
      return;
    }

    try {
      await axios.delete(`/admin-notes/${noteId}`);
      setAdminNotes(adminNotes.filter(note => note.note_id !== noteId));
    } catch (error) {
      console.error("Error deleting note:", error);
      alert('Nie udało się usunąć notatki. Spróbuj ponownie.');
    }
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('pl-PL', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return dateString;
    }
  };

  return (
    <div className="admin-dashboard-panel">
      <AdminSidebar />

      <div className="main-content">
        <div className="admin-dashboard-inner">
          {/* HEADER */}
          <header className="admin-dashboard-header">
            <div>
              <h1 className="admin-dashboard-title">Panel Główny</h1>
              <p className="admin-dashboard-subtitle">
                Zarządzaj całym systemem PSDiA
              </p>
            </div>
          </header>

          {/* KARTY STATYSTYK */}
          <section className="admin-dashboard-stats-grid">
            {statsLoading ? (
              <p>Ładowanie statystyk...</p>
            ) : stats.length > 0 ? (
              stats.map((item) => (
                <article
                  key={item.label}
                  className="admin-dashboard-card admin-dashboard-stat-card"
                >
                  <div className="admin-dashboard-stat-icon-wrap">
                    <item.icon size={22} />
                  </div>
                  <div className="admin-dashboard-stat-content">
                    <p className="admin-dashboard-stat-label">{item.label}</p>
                    <p className="admin-dashboard-stat-value">{item.value}</p>
                    <p className="admin-dashboard-stat-desc">
                      {item.description}
                    </p>
                  </div>
                </article>
              ))
            ) : (
              <p>Brak danych statystycznych</p>
            )}
          </section>

          {/* SZYBKIE AKCJE */}
          <section className="admin-dashboard-card admin-dashboard-quick-actions">
            <h2 className="admin-dashboard-section-title">Szybkie akcje</h2>
            <div className="admin-dashboard-actions-row">
              <Link to="/admin/management" className="admin-dashboard-chip">
                <UserPlus size={16} />
                <span>Dodaj kursanta</span>
              </Link>
              <Link to="/admin/courses" className="admin-dashboard-chip">
                <Group size={16} />
                <span>Utwórz grupę</span>
              </Link>
              <Link to="/admin/messages" className="admin-dashboard-chip">
                <Send size={16} />
                <span>Wyślij powiadomienie</span>
              </Link>
              <Link to="/admin/reports" className="admin-dashboard-chip">
                <FileText size={16} />
                <span>Wygeneruj raport</span>
              </Link>
            </div>
          </section>

          {/* NOTES ADMINÓW + NADCHODZĄCE ZAJĘCIA */}
          <section className="admin-dashboard-main-grid">
            <article className="admin-dashboard-card admin-dashboard-messages">
              <h2 className="admin-dashboard-section-title">Notes Adminów</h2>
              <p className="admin-dashboard-note-helper">
                Prywatna notatka – widoczna tylko dla Ciebie na tym urządzeniu.
              </p>
              <textarea
                className="admin-dashboard-note-textarea"
                placeholder="Zapisz tu ważną rzecz dla siebie..."
                value={adminNote}
                onChange={(e) => setAdminNote(e.target.value)}
              />

              {/* Wewnętrzna komunikacja adminów */}
              <div className="admin-dashboard-admin-notes">
                <div className="admin-dashboard-admin-notes-header">
                  <h3 className="admin-dashboard-admin-notes-title">
                    Wewnętrzna komunikacja adminów
                  </h3>
                  <button
                    className="admin-dashboard-add-note-btn"
                    onClick={() => setShowNoteModal(true)}
                  >
                    <Plus size={16} />
                    <span>Dodaj</span>
                  </button>
                </div>
                {adminNotesLoading ? (
                  <p>Ładowanie notatek...</p>
                ) : adminNotes.length > 0 ? (
                  <ul className="admin-dashboard-admin-notes-list">
                    {adminNotes.map((note) => (
                      <li
                        key={note.note_id}
                        className="admin-dashboard-admin-note-item"
                      >
                        <div className="admin-dashboard-admin-note-content">
                          <div className="admin-dashboard-admin-note-header">
                            <span className="admin-dashboard-admin-note-author">
                              {note.username}
                            </span>
                            <span className="admin-dashboard-admin-note-date">
                              {formatDate(note.created_at)}
                            </span>
                            {currentUserId === note.user_id && (
                              <button
                                className="admin-dashboard-delete-note-btn"
                                onClick={() => handleDeleteNote(note.note_id)}
                                title="Usuń notatkę"
                              >
                                <Trash2 size={14} />
                              </button>
                            )}
                          </div>
                          <span className="admin-dashboard-admin-note-text">
                            {note.content}
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="admin-dashboard-empty-notes">
                    Brak notatek. Kliknij "Dodaj", aby utworzyć pierwszą notatkę.
                  </p>
                )}
              </div>
            </article>

            <article className="admin-dashboard-card admin-dashboard-upcoming">
              <h2 className="admin-dashboard-section-title">
                Nadchodzące zajęcia
              </h2>
              <ul className="admin-dashboard-list">
                {upcomingClassesLoading ? (
                  <li className="admin-dashboard-list-item">
                    Ładowanie zajęć...
                  </li>
                ) : upcomingClasses.length > 0 ? (
                  upcomingClasses.map((classItem) => (
                    <li
                      key={classItem.id}
                      className="admin-dashboard-list-item"
                    >
                      {classItem.display}
                    </li>
                  ))
                ) : (
                  <li className="admin-dashboard-list-item">
                    Brak nadchodzących zajęć
                  </li>
                )}
              </ul>
            </article>
          </section>
        </div>
      </div>

      {showNoteModal && (
        <AdminNoteModal
          isOpen={showNoteModal}
          onClose={() => setShowNoteModal(false)}
          onNoteAdded={handleNoteAdded}
        />
      )}
    </div>
  );
}
